import { post } from '@/api/client';
import { T } from '@/i18n';
import { track } from '@/marketing/analytics';
import { Button } from '@/ui';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { Field, KontoBox, Unavailable, hint, recoveryFailureText, useAvailability } from './shared';

/** POST /account/recovery; the answer is the same neutral 202 whether or not the address is known. */
export default function GlemtPassord() {
  const available = useAvailability();
  const [email, setEmail] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await post('/account/recovery', { email });
      track('password_reset_requested'); // the ask, not whether the address exists (#102)
      setSent(true);
    } catch (ex) {
      setErr(recoveryFailureText(ex));
    } finally {
      setBusy(false);
    }
  }

  // the server says this flow is closed: explain it and say what to do instead, rather
  // than taking an address that will be refused (Codex, 2026-09-16)
  if (available.recovery === false) {
    return (
      <KontoBox title={T('Glemt passord')}>
        <Unavailable
          what={T(
            'Nytt passord kan ikke sendes på e-post ennå. Lederen din kan sette et nytt passord for deg.',
          )}
        />
      </KontoBox>
    );
  }

  return (
    <KontoBox title={T('Glemt passord')}>
      {sent ? (
        <div className="opt" style={{ display: 'grid', gap: 10 }}>
          <output className="al info" style={{ margin: 0 }}>
            {T(
              'Hvis adressen finnes hos oss og er bekreftet, har vi sendt en e-post med en lenke. Lenken gjelder i 60 minutter.',
            )}
          </output>
          <Link to="/login" style={hint}>
            {T('Til innlogging')}
          </Link>
        </div>
      ) : (
        <form className="opt" onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
          <Field
            label={T('E-post')}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            last
          />
          {err && <div className="al dgr">{err}</div>}
          <Button tone="pri" type="submit" disabled={busy}>
            {busy ? '…' : T('Send lenke')}
          </Button>
          <span style={hint}>
            {T(
              'En adresse som aldri er bekreftet kan ikke brukes til å få nytt passord. Logg inn og bekreft adressen først.',
            )}
          </span>
          <Link to="/login" style={hint}>
            {T('Til innlogging')}
          </Link>
        </form>
      )}
    </KontoBox>
  );
}
